import { z } from "zod";

export const APPLICATION_TYPES = ["transfer", "reserve"] as const;
export const ApplicationTypeSchema = z.enum(APPLICATION_TYPES);
export type ApplicationType = z.infer<typeof ApplicationTypeSchema>;

export const APPLICATION_TEMPLATE_STATUSES = ["draft", "open", "closed"] as const;
export const ApplicationTemplateStatusSchema = z.enum(APPLICATION_TEMPLATE_STATUSES);
export type ApplicationTemplateStatus = z.infer<typeof ApplicationTemplateStatusSchema>;

const FieldBase = z.object({
  id: z.string().uuid(),
  label: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  required: z.boolean().default(false),
});

const TextConstraints = {
  minLength: z.number().int().nonnegative().max(10_000).optional(),
  maxLength: z.number().int().positive().max(10_000).optional(),
  minSentences: z.number().int().nonnegative().max(500).optional(),
  maxSentences: z.number().int().positive().max(500).optional(),
};

const TextField = FieldBase.extend({ type: z.literal("short_text"), ...TextConstraints });
const ParagraphField = FieldBase.extend({ type: z.literal("long_text"), ...TextConstraints });
const EmailField = FieldBase.extend({ type: z.literal("email") });
const NumberField = FieldBase.extend({
  type: z.literal("number"),
  min: z.number().optional(),
  max: z.number().optional(),
});
const DateField = FieldBase.extend({ type: z.literal("date") });
const ChoiceField = FieldBase.extend({
  type: z.literal("single_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
});
const MultiChoiceField = FieldBase.extend({
  type: z.literal("multi_choice"),
  options: z.array(z.string().min(1).max(200)).min(1).max(50),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});
const SectionField = FieldBase.extend({ type: z.literal("section_header") });

export const ApplicationFieldSchema = z.discriminatedUnion("type", [
  TextField,
  ParagraphField,
  EmailField,
  NumberField,
  DateField,
  ChoiceField,
  MultiChoiceField,
  SectionField,
]);
export type ApplicationField = z.infer<typeof ApplicationFieldSchema>;
export const ApplicationFieldsSchema = z.array(ApplicationFieldSchema).min(1).max(100);

export const ApplicationTemplateSchema = z.object({
  id: z.number().int(),
  type: ApplicationTypeSchema,
  title: z.string(),
  description: z.string().nullable(),
  targetDepartmentId: z.number().int().positive().nullable(),
  status: ApplicationTemplateStatusSchema,
  fields: ApplicationFieldsSchema,
  createdById: z.number().int().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ApplicationTemplate = z.infer<typeof ApplicationTemplateSchema>;

export const CreateApplicationTemplateSchema = ApplicationTemplateSchema.pick({
  type: true,
  title: true,
  description: true,
  targetDepartmentId: true,
  status: true,
  fields: true,
}).extend({ title: z.string().min(1).max(200), description: z.string().max(2000).nullable() });
export type CreateApplicationTemplate = z.infer<typeof CreateApplicationTemplateSchema>;
export const PatchApplicationTemplateSchema = CreateApplicationTemplateSchema.partial().refine(
  (body) => Object.keys(body).length > 0,
  { message: "At least one field is required" },
);

export const ApplicationAnswerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), value: z.string() }),
  z.object({ type: z.literal("number"), value: z.number() }),
  z.object({ type: z.literal("choice"), value: z.string() }),
  z.object({ type: z.literal("choices"), value: z.array(z.string()) }),
  z.object({ type: z.literal("date"), value: z.string() }),
]);
export type ApplicationAnswer = z.infer<typeof ApplicationAnswerSchema>;
export const ApplicationAnswersSchema = z.record(z.string().uuid(), ApplicationAnswerSchema);
export type ApplicationAnswers = z.infer<typeof ApplicationAnswersSchema>;

export const APPLICATION_STATUSES = [
  "pending",
  "revision_requested",
  "approved",
  "denied",
  "closed",
  "withdrawn",
] as const;
export const ApplicationStatusSchema = z.enum(APPLICATION_STATUSES);
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>;

export const APPLICATION_REVIEW_SIDES = ["source_department", "target_department"] as const;
export const ApplicationReviewSideSchema = z.enum(APPLICATION_REVIEW_SIDES);
export type ApplicationReviewSide = z.infer<typeof ApplicationReviewSideSchema>;
export const APPLICATION_DECISIONS = ["approved", "denied"] as const;
export const ApplicationDecisionSchema = z.enum(APPLICATION_DECISIONS);
export type ApplicationDecision = z.infer<typeof ApplicationDecisionSchema>;

export const ApplicationReviewSchema = z.object({
  id: z.number().int(),
  round: z.number().int(),
  side: ApplicationReviewSideSchema,
  reviewerId: z.number().int(),
  reviewerName: z.string().nullable(),
  decision: ApplicationDecisionSchema,
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type ApplicationReview = z.infer<typeof ApplicationReviewSchema>;

export const ApplicationMessageSchema = z.object({
  id: z.number().int(),
  authorId: z.number().int(),
  authorName: z.string().nullable(),
  type: z.enum(["reply", "revision_request"]),
  body: z.string(),
  createdAt: z.string(),
});
export type ApplicationMessage = z.infer<typeof ApplicationMessageSchema>;

export const ApplicationSchema = z.object({
  id: z.number().int(),
  templateId: z.number().int(),
  templateTitle: z.string(),
  type: ApplicationTypeSchema,
  applicantId: z.number().int(),
  applicantName: z.string().nullable(),
  sourceDepartmentId: z.number().int(),
  sourceDepartmentName: z.string(),
  targetDepartmentId: z.number().int(),
  targetDepartmentName: z.string(),
  status: ApplicationStatusSchema,
  reviewRound: z.number().int(),
  fields: ApplicationFieldsSchema,
  answers: ApplicationAnswersSchema,
  reviews: z.array(ApplicationReviewSchema),
  messages: z.array(ApplicationMessageSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Application = z.infer<typeof ApplicationSchema>;

export const CreateApplicationSchema = z.object({
  templateId: z.number().int().positive(),
  answers: ApplicationAnswersSchema,
});
export const ApplicationReplySchema = z.object({
  body: z.string().trim().min(1).max(5000),
  requestRevision: z.boolean().optional().default(false),
});
export const ApplicationDecisionBodySchema = z.object({
  decision: ApplicationDecisionSchema,
  note: z.string().trim().max(2000).nullable().optional(),
  stipulations: z.string().trim().max(2000).nullable().optional(),
});
export const ApplicationPrivateNoteBodySchema = z.object({
  body: z.string().trim().min(1).max(5000),
});
export const ApplicationRevisionBodySchema = z.object({ body: z.string().trim().min(1).max(5000) });
export const ApplicationResubmitBodySchema = z.object({ answers: ApplicationAnswersSchema });
export const ApplicationPrivateNoteSchema = z.object({
  id: z.number().int(),
  authorId: z.number().int(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});

export const ApplicationDepartmentChoiceSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  isPrimary: z.boolean().optional(),
});
export type ApplicationDepartmentChoice = z.infer<typeof ApplicationDepartmentChoiceSchema>;
